"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TabsWithContent = TabsWithContent;
exports.TabContent = TabContent;
exports.Tabs = Tabs;
exports.getTabStyles = getTabStyles;
exports.getNextTabValue = getNextTabValue;
const jsx_runtime_1 = require("@emotion/react/jsx-runtime");
const change_case_1 = require("change-case");
const react_1 = require("react");
const react_aria_1 = require("react-aria");
const react_router_1 = require("react-router");
const react_router_dom_1 = require("react-router-dom");
const components_1 = require("./");
const Css_1 = require("../Css");
const utils_1 = require("../utils");
const defaultTestId_1 = require("../utils/defaultTestId");
const Icon_1 = require("./Icon");
/**
 * Provides a list of tabs and their content.
 *
 * The caller is responsible for using `selected` / `onChange` to control
 * the current tab.
 *
 * If you want to tease apart Tabs from their TabContent, you can use the `Tab`
 * and `TabContent` components directly.
 */
function TabsWithContent(props) {
    // Do not apply default top padding styles if the tabs are being hidden. This avoids unnecessary white space being added
    const styles = hideTabs(props) ? {} : Css_1.Css.pt3.$;
    return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)(Tabs, { ...props }), (0, jsx_runtime_1.jsx)(TabContent, { ...props, contentXss: { ...styles, ...props.contentXss } })] }));
}
function TabContent(props) {
    const tid = (0, utils_1.useTestIds)(props, "tab");
    const { tabs, contentXss = {} } = props;
    const location = (0, react_router_dom_1.useLocation)();
    const selectedTab = isRouteTabs(props)
        ? props.tabs.find((t) => {
            const paths = Array.isArray(t.path) ? t.path : [t.path];
            return paths.some((p) => !!(0, react_router_1.matchPath)(location.pathname, { path: p, exact: true }));
        }) || tabs[0]
        : props.tabs.find((tab) => tab.value === props.selected) || tabs[0];
    const uniqueValue = uniqueTabValue(selectedTab);
    return (
    // Using FullBleed to allow the tab's bgColor to extend to the edges of the <ScrollableContent /> element.
    // Omit the padding from `FullBleed` if the caller passes in the `paddingLeft/Right` styles.
    (0, jsx_runtime_1.jsx)(components_1.FullBleed, { omitPadding: "paddingLeft" in contentXss || "paddingRight" in contentXss, children: (0, jsx_runtime_1.jsx)("div", { "aria-labelledby": `${uniqueValue}-tab`, id: `${uniqueValue}-tabPanel`, role: "tabpanel", tabIndex: 0, ...tid.panel, css: contentXss, children: isRouteTab(selectedTab) ? (0, jsx_runtime_1.jsx)(react_router_1.Route, { path: selectedTab.path, render: selectedTab.render }) : selectedTab.render() }) }));
}
/** The top list of tabs. */
function Tabs(props) {
    const { ariaLabel, tabs, includeBottomBorder, right, ...others } = props;
    const location = (0, react_router_dom_1.useLocation)();
    const selected = isRouteTabs(props)
        ? uniqueTabValue(props.tabs.find((t) => !!(0, react_router_1.matchPath)(location.pathname, { path: t.path, exact: true })) || props.tabs[0])
        : props.selected;
    const { isFocusVisible, focusProps } = (0, react_aria_1.useFocusRing)();
    const tid = (0, utils_1.useTestIds)(others, "tabs");
    const [active, setActive] = (0, react_1.useState)(selected);
    const ref = (0, react_1.useRef)(null);
    // Whenever selected changes, reset active
    (0, react_1.useEffect)(() => setActive(selected), [selected]);
    // the active tab is highlighted, but not necessarily "selected"
    // the selected tab dictates what is displayed in the content panel
    function onKeyUp(e) {
        var _a;
        // left and right arrow keys update the active tab
        if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
            const nextTabValue = getNextTabValue(active, e.key, tabs);
            setActive(nextTabValue);
            // Ensure the browser's focus follows the active element.
            (_a = document.getElementById(`${nextTabValue}-tab`)) === null || _a === void 0 ? void 0 : _a.focus();
        }
    }
    // clicking on a tab sets it to selected and active
    function onClick(value) {
        !isRouteTabs(props) && props.onChange(value);
    }
    // bluring out resets active to whatever selected is
    function onBlur(e) {
        // only reset active state if we've moved focus out of the tab list
        if (!(ref.current && ref.current.contains(e.relatedTarget))) {
            setActive(selected);
        }
    }
    return ((0, jsx_runtime_1.jsxs)("div", { css: { ...Css_1.Css.df.aic.oa.wsnw.gap1.$, ...(includeBottomBorder ? { ...Css_1.Css.bb.bcGray200.$ } : {}) }, children: [!hideTabs(props) && ((0, jsx_runtime_1.jsx)("div", { ref: ref, css: Css_1.Css.dif.gap1.asfe.$, "aria-label": ariaLabel, role: "tablist", ...tid, children: tabs.map((tab) => {
                    const uniqueValue = uniqueTabValue(tab);
                    return ((0, jsx_runtime_1.jsx)(TabImpl, { active: active === uniqueValue, focusProps: focusProps, isFocusVisible: isFocusVisible, onClick: onClick, onKeyUp: onKeyUp, onBlur: onBlur, tab: tab, ...tid[(0, defaultTestId_1.defaultTestId)(uniqueValue)] }, uniqueValue));
                }) })), right && (0, jsx_runtime_1.jsx)("div", { css: Css_1.Css.mla.df.aic.gap1.pb1.$, children: right })] }));
}
function TabImpl(props) {
    const { tab, onClick, active, onKeyUp, onBlur, focusProps, isFocusVisible = false, ...others } = props;
    const { disabled = false, name: label, icon, endAdornment } = tab;
    const isDisabled = !!disabled;
    const { hoverProps, isHovered } = (0, react_aria_1.useHover)({ isDisabled });
    const { baseStyles, activeStyles, focusRingStyles, hoverStyles, disabledStyles, activeHoverStyles } = (0, react_1.useMemo)(() => getTabStyles(), []);
    const uniqueValue = uniqueTabValue(tab);
    const tabProps = {
        "aria-controls": `${uniqueValue}-tabPanel`,
        "aria-selected": active,
        "aria-disabled": isDisabled || undefined,
        id: `${uniqueValue}-tab`,
        role: "tab",
        tabIndex: active ? 0 : -1,
        ...others,
        css: {
            ...baseStyles,
            ...(active && activeStyles),
            ...(isDisabled && disabledStyles),
            ...(isHovered && hoverStyles),
            ...(isHovered && active && activeHoverStyles),
            ...(isFocusVisible && active && focusRingStyles),
        },
    };
    const interactiveProps = (0, react_aria_1.mergeProps)(focusProps, hoverProps, {
        onKeyUp,
        onBlur,
        ...(isRouteTab(tab) ? {} : { onClick: () => onClick(tab.value) }),
    });
    const tabLabel = ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [label, (icon || endAdornment) && (0, jsx_runtime_1.jsx)("span", { css: Css_1.Css.ml1.$, children: icon ? (0, jsx_runtime_1.jsx)(Icon_1.Icon, { icon: icon }) : endAdornment })] }));
    return isDisabled ? ((0, components_1.maybeTooltip)({
        title: (0, components_1.resolveTooltip)(disabled),
        placement: "top",
        children: (0, jsx_runtime_1.jsx)("div", { ...tabProps, children: tabLabel }),
    })) : isRouteTab(tab) ? ((0, jsx_runtime_1.jsx)(react_router_dom_1.Link, { ...tabProps, ...interactiveProps, className: "navLink", to: tab.href, children: tabLabel })) : ((0, jsx_runtime_1.jsx)("button", { ...tabProps, ...interactiveProps, children: tabLabel }));
}
function getTabStyles() {
    const borderBottomWidthPx = 4;
    const verticalPaddingPx = 6;
    // Decrease the bottom padding by the same amount of the new border width to prevent text layout shift
    const borderBottomStyles = Css_1.Css.bb
        .add("borderBottomWidth", `${borderBottomWidthPx}px`)
        .pbPx(verticalPaddingPx - borderBottomWidthPx).$;
    return {
        baseStyles: Css_1.Css.df.aic.hPx(32).pyPx(verticalPaddingPx).px1.outline0.gray700.add("width", "fit-content")
            .cursorPointer.sm.$,
        activeStyles: Css_1.Css.add(borderBottomStyles).bcBlue700.smMd.gray900.$,
        disabledStyles: Css_1.Css.gray400.cursorNotAllowed.$,
        focusRingStyles: Css_1.Css.bgBlue50.bshFocus.$,
        hoverStyles: Css_1.Css.add(borderBottomStyles).bcGray400.$,
        activeHoverStyles: Css_1.Css.bgBlue50.add(borderBottomStyles).bcBlue700.$,
    };
}
function getNextTabValue(selected, key, tabs) {
    const enabledTabs = tabs.filter((tab) => tab.disabled !== true);
    const tabsToScan = key === "ArrowRight" ? enabledTabs : enabledTabs.reverse();
    const currentIndex = tabsToScan.findIndex((tab) => uniqueTabValue(tab) === selected);
    const nextIndex = currentIndex === tabsToScan.length - 1 ? 0 : currentIndex + 1;
    return uniqueTabValue(tabsToScan[nextIndex]);
}
function isRouteTabs(props) {
    const { tabs } = props;
    return tabs.length > 0 && isRouteTab(tabs[0]);
}
function isRouteTab(tab) {
    return "path" in tab;
}
function uniqueTabValue(tab) {
    return isRouteTab(tab) ? (0, change_case_1.camelCase)(tab.name) : tab.value;
}
// Determines whether we should hide the Tab panel. Returns true if there is only one enabled tab and `alwaysShowAllTabs` is falsey.
function hideTabs(props) {
    return props.alwaysShowAllTabs ? false : props.tabs.filter((t) => !t.disabled).length === 1;
}
